import { useEffect, useState } from 'react';

/**
 * Rileva quando è online una versione dell'app più recente di quella caricata.
 *
 * L'app carica il suo codice una volta sola e lo usa finché la pagina non viene
 * ricaricata: una scheda lasciata aperta per giorni (su iPad succede di continuo)
 * resta ferma alla versione vecchia anche dopo un deploy, e chi la usa continua
 * a vedere problemi già corretti. È successo a settembre 2026: la correzione del
 * form amichetti era online, ma il cliente continuava a usare la versione precedente.
 *
 * Come funziona: il file principale ha l'hash del contenuto nel nome
 * (`/assets/index-<hash>.js`), che cambia a ogni deploy che modifica il codice.
 * Si confronta quello caricato da questa pagina con quello citato dall'HTML appena
 * servito dal server (che esce con `Cache-Control: no-cache`, vedi server/src/static.ts).
 */

const ENTRY_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/;
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
/** Tra focus e cambio di visibilità possono arrivare più eventi insieme: un controllo al minuto basta */
const MIN_GAP_MS = 60 * 1000;

/** Il file principale caricato da questa pagina, o null in sviluppo (Vite non usa hash) */
function loadedEntry(): string | null {
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]');
  for (const script of Array.from(scripts)) {
    const match = script.getAttribute('src')?.match(ENTRY_RE);
    if (match) return match[0];
  }
  return null;
}

/** Il file principale citato dall'HTML attualmente servito dal server */
async function liveEntry(): Promise<string | null> {
  const res = await fetch('/', { cache: 'no-store', credentials: 'same-origin' });
  if (!res.ok) return null;
  const match = (await res.text()).match(ENTRY_RE);
  return match ? match[0] : null;
}

export function useNewVersionAvailable(): boolean {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    const current = loadedEntry();
    if (!current) return; // in sviluppo non c'è niente da confrontare

    let lastCheck = 0;
    let found = false;

    const check = async () => {
      if (found || Date.now() - lastCheck < MIN_GAP_MS) return;
      lastCheck = Date.now();
      try {
        const live = await liveEntry();
        if (live && live !== current) {
          found = true;
          setAvailable(true);
        }
      } catch {
        // Rete assente o server a metà deploy: si riprova al giro successivo.
      }
    };

    // Il momento che conta è il rientro nella scheda dopo ore o giorni:
    // lì si controlla subito, oltre al giro periodico.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };
    const interval = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', check);

    return () => {
      found = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', check);
    };
  }, []);

  return available;
}
